import {UserState, useUser} from '@aboutyou/user';
import {useEffect, useRef} from 'react';
import {useIsCustomerSessionValid} from '../../Checkout/useIsCustomerSessionValid/useIsCustomerSessionValid';

export const useIsAuthenticated = () => {
  const {data: user, isLoading: isUserDataLoading} = useUser();

  // eslint-disable-next-line todo-plz/ticket-ref
  // TODO: make sure user types can work with new situations => token is here but isLoggedIn is false
  const isLoggedIn = Boolean(user && 'token' in user && user.token);

  const {isValidating, canAccessCheckout, canAccessShop} =
    useIsCustomerSessionValid();

  const unblockAuthCallback = useRef<((value: AuthAccessFlags) => void) | null>(
    null,
  );

  /**
   * This effect makes sure that any isAuthenticatedCallback waiting for the "resume" request to finish will be unblocked
   */
  useEffect(() => {
    if (isValidating === false) {
      unblockAuthCallback.current?.({
        canAccessCheckout,
        canAccessShop,
      });
    }
  }, [canAccessCheckout, canAccessShop, isValidating]);

  return {
    isUserDataLoading: Boolean(isUserDataLoading || isValidating),
    isAuthenticated: isLoggedIn,
    isAuthenticatedCallback: async ({
      needsCheckoutSession,
    }: {
      needsCheckoutSession?: boolean;
    }): Promise<{
      isAuthenticated: boolean;
      reason: 'guest' | 'resume-validation';
      user: UserState | undefined;
    }> => {
      return new Promise(async resolve => {
        if (!isLoggedIn) {
          resolve({
            isAuthenticated: false,
            reason: 'guest',
            user,
          });
          return;
        }

        if (isValidating === false) {
          if (needsCheckoutSession) {
            resolve({
              isAuthenticated: canAccessCheckout,
              reason: 'resume-validation',
              user,
            });
            return;
          } else {
            resolve({
              isAuthenticated: canAccessShop,
              reason: 'resume-validation',
              user,
            });
            return;
          }
        }

        /**
         * If the "resume" request is still in progress, we need to wait for it to finish before we can say if the user is authenticated or not.
         */
        const resumeResponse = await new Promise<AuthAccessFlags>(resolve => {
          unblockAuthCallback.current = resolve;
        });

        // reset unblockAuthCallback to avoid memory leaks
        unblockAuthCallback.current = null;

        if (resumeResponse.canAccessCheckout) {
          resolve({
            isAuthenticated: resumeResponse.canAccessCheckout,
            reason: 'resume-validation',
            user,
          });
          return;
        }
        resolve({
          isAuthenticated: resumeResponse.canAccessShop,
          reason: 'resume-validation',
          user,
        });
      });
    },
  };
};

type AuthAccessFlags = {
  canAccessCheckout: boolean;
  canAccessShop: boolean;
};
